import { updateShippingQuotes } from '@theme/b2b-shipping';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class BulkOrderGrid extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.config = this.#readJson('[data-bulk-order-config]');
    for (const key of Object.keys(this.config.labels || {})) {
      this.config.labels[key] = String(this.config.labels[key]).replace(/\[(count|discount|group_count|cart_count|total)\]/g, '{{ $1 }}');
    }
    this.variants = this.#readJson('[data-bulk-order-variants]');
    this.tiers = (this.config.tiers || [])
      .filter((tier) => tier.enabled && tier.quantity > 0 && tier.discount > 0)
      .sort((a, b) => a.quantity - b.quantity);
    this.quantities = new Map();
    this.cartLines = [];
    this.otherGroupQuantity = 0;
    this.activeGroup = '';
    this.#bindStaticEvents();
    this.#initialize();
    const purchase = this.querySelector('[data-sticky-enabled="true"]');
    if (purchase && !this.closest('quick-add-dialog')) {
      this.componentVisible = true;
      this.stickyObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === (this.closest('.product-information') || this)) this.componentVisible = entry.isIntersecting;
          else this.offerAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        }
        const stuck = this.componentVisible && this.offerAboveViewport;
        purchase.dataset.stuck = String(stuck);
        this.dataset.purchaseStuck = String(stuck);
      });
      this.stickyObserver.observe(this.querySelector('[data-tier-list]'));
      this.stickyObserver.observe(this.closest('.product-information') || this);
    }
  }

  disconnectedCallback() {
    this.stickyObserver?.disconnect();
    clearTimeout(this.successTimer);
    this.imagePreview?.remove();
  }

  #readJson(selector) {
    const node = this.querySelector(selector);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error('[bulk-order] Invalid component data', error);
      return {};
    }
  }

  async #initialize() {
    try {
      const cart = await this.#fetchCart();
      this.#syncFromCart(cart);
      this.#render();
    } catch (error) {
      this.#render();
      this.#showError(this.config.labels.networkError);
    }
  }

  #bindStaticEvents() {
    this.querySelector('[data-submit]')?.addEventListener('click', () => this.#submit());
  }

  async #fetchCart() {
    const response = await fetch(this.#jsonUrl(this.dataset.cartUrl), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  #syncFromCart(cart) {
    const items = cart.items || [];
    this.cartLines = items.filter((item) => String(item.product_id) === String(this.config.productId));
    const groupLines = items.filter((item) => {
      const group = item.properties?._b2b_pricing_group;
      return String(group || '') === String(this.config.pricingGroup || '')
        || String(item.product_id) === String(this.config.productId);
    });
    this.otherGroupQuantity = groupLines
      .filter((item) => String(item.product_id) !== String(this.config.productId))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    this.quantities.clear();
    for (const line of this.cartLines) {
      const id = String(line.variant_id);
      this.quantities.set(id, (this.quantities.get(id) || 0) + Number(line.quantity || 0));
    }
  }

  #canOrder(variant) {
    return !variant.inventoryManagement || variant.inventoryPolicy === 'continue' || Boolean(variant.available);
  }

  #hasTrackedLimit(variant) {
    return Boolean(variant.inventoryManagement) && variant.inventoryPolicy !== 'continue';
  }

  #groups() {
    const groups = new Map();
    const count = Math.max(0, (this.config.optionNames || []).length - 1);
    for (const variant of this.variants) {
      const options = variant.options || [];
      const groupName = count === 1 ? options[0] : JSON.stringify(options.slice(0, count));
      const value = options.at(-1);
      const rowName = value && value !== 'Default Title' ? value : this.config.productTitle;
      if (!groups.has(groupName)) groups.set(groupName, []);
      groups.get(groupName).push({ ...variant, groupName, rowName });
    }
    return groups;
  }

  #optionRows() {
    const names = this.config.optionNames || [];
    this.selectedOptions ||= [];
    let html = '';
    for (let index = 0; index < names.length - 1; index++) {
      const candidates = this.variants.filter(v => v.options.slice(0, index).every((value, i) => value === this.selectedOptions[i]));
      const values = [...new Set(candidates.map(v => v.options[index]))];
      const available = value => candidates.some(v => v.options[index] === value && this.#canOrder(v));
      if (!values.includes(this.selectedOptions[index]) || !available(this.selectedOptions[index])) this.selectedOptions[index] = values.find(available) || values[0];
      values.sort((a,b) => Number(available(b)) - Number(available(a)));
      html += `<div class="bulk-order__option"><h3>${this.#escape(names[index])}</h3><div class="bulk-order__tabs" role="group" aria-label="${this.#escape(names[index])}">${values.map(value => {
        const variants = candidates.filter(v => v.options[index] === value);
        return this.#renderTab(value, variants, index);
      }).join('')}</div></div>`;
    }
    const prefix = this.selectedOptions.slice(0, Math.max(0,names.length - 1));
    this.activeGroup = prefix.length === 1 ? prefix[0] : JSON.stringify(prefix);
    return html;
  }

  #render() {
    const picker = this.querySelector('[data-picker]');
    if (!picker || !Array.isArray(this.variants)) return;
    const colorScroll = picker.querySelector('.bulk-order__tabs')?.scrollLeft || 0;
    const groups = this.#groups();
    const optionRows = this.#optionRows();
    picker.innerHTML = `${optionRows}
      <div class="bulk-order__sizes-heading" ${this.config.quickPackEnabled ? '' : 'hidden'}>
        ${this.config.quickPackEnabled ? `<button type="button" class="bulk-order__quick-pack" data-quick-pack>${this.#escape(this.config.labels.quickPack)} (${Number(this.config.quickPackSizes) * Number(this.config.quickPackEach)} ${this.#escape(this.config.labels.units)})</button>` : ''}
      </div>
      <div class="bulk-order__sizes" role="tabpanel">
        <div class="bulk-order__table-head">
          <span>${this.#escape(this.config.optionNames?.at(-1) === 'Title' ? this.config.labels.allVariants : this.config.optionNames?.at(-1) || this.config.labels.size)}</span>
          <span>${this.#escape(this.config.labels.stock)}</span>
          <span>${this.#escape(this.config.labels.unitPrice)}</span>
          <span class="bulk-order__quantity-heading">${this.#escape(this.config.labels.quantity)} <button type="button" data-reset-color aria-label="${this.#escape(this.config.labels.resetColor)}" title="${this.#escape(this.config.labels.resetColor)}"><span aria-hidden="true">↺</span></button></span>
        </div>
        <div data-size-rows>${this.#renderRows(groups.get(this.activeGroup) || [])}</div>
      </div>`;
    picker.setAttribute('aria-busy', 'false');
    if (picker.querySelector('.bulk-order__tabs')) picker.querySelector('.bulk-order__tabs').scrollLeft = colorScroll;
    this.#bindPickerEvents();
    this.#updateSummary();
  }

  #renderTab(name, variants, index) {
    const selected = name === this.selectedOptions[index];
    const available = variants.some((variant) => this.#canOrder(variant));
    const image = variants.find((variant) => variant.featuredImage)?.featuredImage;
    const count = variants.reduce((sum, variant) => sum + (this.quantities.get(String(variant.id)) || 0), 0);
    const visual = image
      ? `<img class="bulk-order__tab-image" src="${this.#escape(image)}" alt="" width="42" height="48" loading="lazy">`
      : `<span class="bulk-order__tab-swatch" aria-hidden="true">${this.#escape(String(name).slice(0, 2).toUpperCase())}</span>`;
    const state = count
      ? this.config.labels.selected.replace('{{ count }}', String(count))
      : !available ? this.config.labels.soldOut : this.config.labels.viewSizes;
    return `<button type="button" class="bulk-order__tab" aria-pressed="${selected}" ${available ? '' : 'disabled'} data-option-index="${index}" data-has-quantity="${count > 0}" data-sold-out="${!available}"  data-group="${this.#escape(name)}">
      ${visual}<span class="bulk-order__tab-copy"><span class="bulk-order__tab-name">${this.#escape(name)}</span><span class="bulk-order__tab-count">${this.#escape(state)}</span></span>
    </button>`;
  }

  #renderRows(variants) {
    return variants.map((variant) => {
      const id = String(variant.id);
      const quantity = this.quantities.get(id) || 0;
      const inventory = Number(variant.inventoryQuantity);
      const canOrder = this.#canOrder(variant);
      const hasTrackedLimit = this.#hasTrackedLimit(variant);
      const lowStock = canOrder && variant.inventoryManagement && inventory > 0 && inventory < 10;
      const stock = !canOrder
        ? this.config.labels.soldOut
        : lowStock
          ? this.config.labels.onlyLeft.replace('{{ count }}', String(inventory))
          : this.config.labels.inStock;
      const maximum = hasTrackedLimit && inventory >= 0 ? `max="${inventory}"` : '';
      return `<div class="bulk-order__size-row" data-variant-id="${id}" data-has-quantity="${quantity > 0}" data-unavailable="${!canOrder}">
        <span><span class="bulk-order__size-name">${this.#escape(variant.rowName)}</span>${this.config.showSku && variant.sku ? `<span class="bulk-order__sku">${this.#escape(variant.sku)}</span>` : ''}</span>
        <span class="bulk-order__stock" data-low-stock="${lowStock}">${this.#escape(stock)}</span>
        <span class="bulk-order__price" data-price="${variant.price}">${this.#formatVariantPrice(variant.price)}</span>
        <span class="bulk-order__quantity">
          <button type="button" data-delta="-1" aria-label="${this.#escape(this.config.labels.decreaseQuantity)}" ${!canOrder || quantity <= 0 ? 'disabled' : ''}>−</button>
          <input type="number" inputmode="numeric" min="0" ${maximum} step="1" value="${quantity}" aria-label="${this.#escape(`${variant.rowName} ${this.config.labels.quantity}`)}" ${!canOrder ? 'disabled' : ''}>
          <button type="button" data-delta="1" aria-label="${this.#escape(this.config.labels.increaseQuantity)}" ${!canOrder || (hasTrackedLimit && quantity >= inventory) ? 'disabled' : ''}>+</button>
        </span>
      </div>`;
    }).join('');
  }

  #bindPickerEvents() {
    this.querySelector('[data-reset-color]')?.addEventListener('click', () => {
      for (const variant of this.#groups().get(this.activeGroup) || []) this.quantities.set(String(variant.id), 0);
      this.#hideError();
      this.#render();
      this.querySelector('[data-reset-color]')?.focus({ preventScroll: true });
    });
    this.querySelector('[data-quick-pack]')?.addEventListener('click', () => {
      const variants = (this.#groups().get(this.activeGroup) || []).filter((variant) => this.#canOrder(variant)).slice(0, Number(this.config.quickPackSizes));
      for (const variant of variants) {
        const id = String(variant.id);
        const maximum = this.#hasTrackedLimit(variant) ? Math.max(0, Number(variant.inventoryQuantity)) : Infinity;
        this.quantities.set(id, Math.min(maximum, (this.quantities.get(id) || 0) + Number(this.config.quickPackEach)));
      }
      this.#hideError();
      this.#render();
    });
    for (const button of this.querySelectorAll('[data-option-index]')) {
      button.addEventListener('click', (event) => {
        const preview = event.target.closest('.bulk-order__tab-image');
        const previewVariant = preview ? this.variants.find(v => v.featuredImage === preview.getAttribute('src')) : null;
        const index = Number(button.dataset.optionIndex);
        const value = button.dataset.group;
        this.selectedOptions[index] = value;
        this.#render();
        [...this.querySelectorAll('[data-option-index]')].find(node => Number(node.dataset.optionIndex) === index && node.dataset.group === value)?.focus({ preventScroll: true });
        const variant = this.#groups().get(this.activeGroup)?.find(v => this.#canOrder(v) && v.featuredMediaId);
        this.#syncMedia(variant?.featuredMediaId);
        if (previewVariant) this.#previewImage(previewVariant.previewImage || previewVariant.featuredImage, value);
      });
    }
    for (const row of this.querySelectorAll('[data-variant-id]')) {
      const input = row.querySelector('input');
      row.querySelectorAll('[data-delta]').forEach((button) => button.addEventListener('click', () => {
        input.value = String(Number(input.value || 0) + Number(button.dataset.delta || 0));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      input?.addEventListener('change', () => this.#setQuantity(row.dataset.variantId, input));
      input?.addEventListener('input', () => this.#setQuantity(row.dataset.variantId, input));
    }
  }

  #syncMedia(mediaId) {
    if (!mediaId) return;
    const scope = this.closest('.shopify-section, dialog') || document;
    const gallery = scope.querySelector('media-gallery');
    if (!gallery?.slideshow) return;
    const slides = [...gallery.querySelectorAll('slideshow-component:not(.dialog-zoomed-gallery) slideshow-slide')].filter(slide => !slide.closest('dialog'));
    const index = slides.findIndex(slide => [...slide.querySelectorAll('[data-media-id]')].some(media => String(media.dataset.mediaId) === String(mediaId)));
    if (index >= 0) gallery.slideshow.select(index, undefined, { animate: true });
    // Desktop grid layouts have no visible carousel; bring the matching media into view.
    if (matchMedia('(min-width:750px)').matches && gallery.classList.contains('media-gallery--grid')) {
      const target = [...gallery.querySelectorAll('.media-gallery__grid [data-media-id]')].find(node => String(node.dataset.mediaId) === String(mediaId));
      target?.scrollIntoView({ block:'nearest', behavior:'smooth' });
    }
  }

  #previewImage(src, label) {
    if (!this.imagePreview) {
      this.imagePreview = document.createElement('dialog');
      this.imagePreview.className = 'bulk-order__image-preview';
      this.imagePreview.setAttribute('aria-label', this.config.productTitle);
      this.imagePreview.tabIndex = -1;
      this.imagePreview.append(document.createElement('img'));
      this.imagePreview.addEventListener('click', () => this.imagePreview.close());
      this.imagePreview.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this.imagePreview.close(); } });
      this.append(this.imagePreview);
    }
    const image = this.imagePreview.querySelector('img'); image.src = src; image.alt = label;
    this.imagePreview.showModal();
  }

  #setQuantity(variantId, input) {
    const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    const value = Math.max(0, Math.min(maximum, Math.floor(Number(input.value) || 0)));
    input.value = String(value);
    this.quantities.set(String(variantId), value);
    this.#hideError();
    const row = input.closest('[data-variant-id]');
    row.dataset.hasQuantity = String(value > 0);
    row.querySelector('[data-delta="-1"]').disabled = value <= 0;
    row.querySelector('[data-delta="1"]').disabled = value >= maximum;
    this.#updateSummary();
  }

  #activeTier(total) {
    return [...this.tiers].reverse().find((tier) => total >= tier.quantity) || null;
  }

  #nextTier(total) {
    return this.tiers.find((tier) => total < tier.quantity) || null;
  }

  #totals() {
    const quantity = [...this.quantities.values()].reduce((sum, value) => sum + value, 0);
    const pricingQuantity = this.otherGroupQuantity + quantity;
    const raw = this.variants.reduce((sum, variant) => sum + Number(variant.price || 0) * (this.quantities.get(String(variant.id)) || 0), 0);
    const tier = this.#activeTier(pricingQuantity);
    const estimated = Math.round(raw * (100 - Number(tier?.discount || 0)) / 100);
    const freight = 0;
    return { quantity, pricingQuantity, raw, estimated, savings: raw - estimated, freight, landed: estimated + freight, tier };
  }

  #renderTiers() {
    const list = this.querySelector('[data-tier-list]');
    if (!list) return;
    const { pricingQuantity, tier: active } = this.#totals();
    const displayTiers = this.config.showBaseTier ? [{ quantity: 1, discount: 0 }, ...this.tiers] : this.tiers;
    list.style.setProperty('--tier-count', String(Math.max(1, displayTiers.length)));
    const baseVariant = [...this.variants].sort((a,b) => a.price - b.price)[0];
    let comparison = this.querySelector('[data-sale-comparison]');
    if (!comparison) { comparison = document.createElement('div'); comparison.dataset.saleComparison = ''; list.before(comparison); }
    comparison.innerHTML = baseVariant?.compareAtPrice > baseVariant?.price ? `<del>${this.#formatVariantPrice(baseVariant.compareAtPrice)}</del> <strong>${this.#formatVariantPrice(baseVariant.price)}</strong>` : '';
    list.innerHTML = displayTiers.map((tier, index) => {
      const price = Math.round(Number(this.config.priceMin || 0) * (100 - tier.discount) / 100);
      const current = active === tier || (!active && tier.discount === 0);
      const next = displayTiers[index + 1];
      const range = next ? `${tier.quantity} - ${next.quantity - 1}` : `${tier.quantity}+`;
      return `<div class="bulk-order__tier" aria-current="${current}" data-reached="${pricingQuantity >= tier.quantity}">
        ${tier.discount > 0 ? `<span class="bulk-order__tier-discount">-${tier.discount}%</span>` : ''}
        <span class="bulk-order__tier-quantity">${range} ${this.#escape(this.config.labels.units)}</span>
        <span class="bulk-order__tier-price">${this.config.priceVaries ? this.#escape(this.config.labels.from) + ' ' : ''}${this.#formatVariantPrice(price)}</span>
      </div>`;
    }).join('');
    list.closest('.bulk-order__offer')?.toggleAttribute('hidden', this.tiers.length === 0);
  }

  #setText(selector, value) {
    const node = this.querySelector(selector);
    if (node) node.textContent = String(value);
  }

  #updateSummary() {
    const totals = this.#totals();
    const next = this.#nextTier(totals.pricingQuantity);
    const shippingScope = this.closest('quick-add-dialog, .product-information, .shopify-section') || this.parentElement;
    if (shippingScope) updateShippingQuotes(shippingScope, this.variants.map(variant => ({ quantity: this.quantities.get(String(variant.id)) || 0, grams: variant.weight })), { perPiece: totals.quantity === 0 });
    this.#setText('[data-selected-total]', totals.quantity);
    this.#setText('[data-mixed-total]', totals.pricingQuantity);
    this.#setText('[data-current-discount]', `${totals.tier?.discount || 0}%`);
    this.#setText('[data-estimated-subtotal]', this.#formatVariantPrice(totals.estimated));
    this.#setText('[data-estimated-savings]', this.#formatVariantPrice(totals.savings));
    this.#setText('[data-estimated-freight]', this.config.labels.shippingPending || this.config.labels.atCheckout);
    this.#setText('[data-estimated-total]', this.#formatVariantPrice(totals.landed));
    this.#setText('[data-raw-total]', this.#formatVariantPrice(totals.raw));
    if (this.config.msrp > 0) this.#setText('[data-profit]', this.#formatVariantPrice(Math.max(0, Math.round(this.config.msrp - (totals.quantity ? totals.estimated / totals.quantity : this.config.priceMin || 0)))));
    this.querySelector('[data-raw-total]')?.toggleAttribute('hidden', totals.savings <= 0);
    const target = next?.quantity || this.tiers.at(-1)?.quantity || 1;
    const meter = this.querySelector('[data-tier-meter]');
    if (meter) { meter.max = target; meter.value = Math.min(target, totals.pricingQuantity); }
    this.#setText('[data-progress-count]', `${totals.pricingQuantity} / ${target}`);
    for (const tab of this.querySelectorAll('[data-option-index]')) {
      const index = Number(tab.dataset.optionIndex);
      const variants = this.variants.filter(v => v.options[index] === tab.dataset.group && v.options.slice(0,index).every((value,i) => value === this.selectedOptions[i]));
      const count = variants.reduce((sum,v) => sum + (this.quantities.get(String(v.id)) || 0),0);
      tab.dataset.hasQuantity = String(count > 0);
      tab.querySelector('.bulk-order__tab-count').textContent = count ? this.config.labels.selected.replace('{{ count }}', String(count)) : tab.disabled ? this.config.labels.soldOut : this.config.labels.viewSizes;
    }

    const progress = this.querySelector('[data-tier-progress]');
    if (progress) {
      if (totals.quantity > 0 && totals.quantity < this.config.minimum) {
        progress.textContent = this.config.labels.minimumError.replace('{{ count }}', String(this.config.minimum - totals.quantity));
      } else if (next) {
        progress.textContent = this.config.labels.nextTier
          .replace('{{ count }}', String(next.quantity - totals.pricingQuantity))
          .replace('{{ discount }}', String(next.discount));
      } else if (totals.tier) {
        progress.textContent = this.config.labels.currentTier.replace('{{ discount }}', String(totals.tier.discount));
      } else {
        progress.textContent = this.config.labels.noDiscount;
      }
    }

    const mixNote = this.querySelector('[data-mix-note]');
    if (mixNote) {
      mixNote.textContent = this.otherGroupQuantity > 0
        ? this.config.labels.mixedInCart.replace('{{ count }}', String(this.otherGroupQuantity))
        : this.config.labels.mixEligible;
    }

    const button = this.querySelector('[data-submit]');
    if (button) {
      button.disabled = this.busy || this.successActive || totals.quantity === 0 || (totals.quantity > 0 && totals.quantity < this.config.minimum);
      this.#setText('[data-submit-label]', this.successActive ? this.successLabel : this.busy ? this.config.labels.updating : this.cartLines.length ? this.config.labels.updateCart : this.config.labels.addToCart);
    }
    const cartState = this.querySelector('[data-cart-state]');
    if (cartState) {
      const current = this.cartLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
      cartState.hidden = current === 0;
      cartState.textContent = this.config.labels.productInCart.replace('{{ count }}', String(current));
    }
    this.#updateVisiblePrices(totals.tier?.discount || 0);
    this.#renderTiers();
  }

  #updateVisiblePrices(discount) {
    for (const price of this.querySelectorAll('[data-price]')) {
      const raw = Number(price.dataset.price || 0);
      price.textContent = this.#formatVariantPrice(Math.round(raw * (100 - discount) / 100));
    }
  }

  #formatVariantPrice(value) {
    return formatMoney(Number(value || 0), this.config.moneyFormat, this.config.currency);
  }

  async #submit() {
    if (this.busy || this.successActive) return;
    const totals = this.#totals();
    if (totals.quantity === 0) return this.#showError(this.config.labels.selectOne);
    if (totals.quantity < this.config.minimum) {
      return this.#showError(this.config.labels.minimumError.replace('{{ count }}', String(this.config.minimum - totals.quantity)));
    }
    const wasUpdate = this.cartLines.length > 0;
    const button = this.querySelector('[data-submit]');
    this.busy = true;
    this.querySelector('[data-picker]')?.setAttribute('inert', '');
    if (button) {
      button.disabled = true;
      this.#setText('[data-submit-label]', this.config.labels.updating);
    }
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: this.cartLines.length ? 'update' : 'add',
      context: 'product',
      lines: this.variants.map((variant) => ({ merchandiseId: String(variant.id), quantity: this.quantities.get(String(variant.id)) || 0 })),
      promise: deferred.promise,
    }));
    try {
      this.#hideError();
      const desired = new Map(this.quantities);
      await this.#writeCart();
      const cart = await this.#fetchCart();
      const saved = new Map();
      for (const item of cart.items || []) {
        if (String(item.product_id) !== String(this.config.productId)) continue;
        const id = String(item.variant_id);
        saved.set(id, (saved.get(id) || 0) + Number(item.quantity));
      }
      for (const id of new Set([...desired.keys(), ...saved.keys()])) {
        if ((desired.get(id) || 0) !== (saved.get(id) || 0)) throw new Error(this.config.labels.saveMismatch);
      }
      this.#syncFromCart(cart);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, source: 'bulk-order-grid', sourceId: this.config.productId, itemCount: totals.quantity, productId: this.config.productId },
      });
      if (this.closest('quick-add-dialog') && document.querySelector('b2b-cart')) {
        window.location.reload();
        return;
      }
      this.#showSuccess(this.#totals(), wasUpdate);
      this.#render();
    } catch (error) {
      deferred.resolve({ detail: { didError: true, source: 'bulk-order-grid', sourceId: this.config.productId } });
      this.dispatchEvent(new CartErrorEvent({ error: error.message || this.config.labels.inventoryError, code: 'INVALID' }));
      this.#showError(error.message || this.config.labels.inventoryError);
      if (button) button.disabled = false;
    } finally {
      this.busy = false;
      this.querySelector('[data-picker]')?.removeAttribute('inert');
      this.#updateSummary();
    }
  }

  async #writeCart() {
    const existingByVariant = new Map();
    for (const line of this.cartLines) {
      const variantId = String(line.variant_id);
      if (!existingByVariant.has(variantId)) existingByVariant.set(variantId, []);
      existingByVariant.get(variantId).push(line);
    }
    if (this.cartLines.length) {
      const updates = {};
      for (const [variantId, lines] of existingByVariant.entries()) {
        const desired = this.quantities.get(variantId) || 0;
        const managedIndex = lines.findIndex((line) => String(line.properties?._b2b_pricing_group || '') === String(this.config.pricingGroup));
        lines.forEach((line, index) => { updates[line.key] = index === managedIndex ? desired : 0; });
      }
      const updateResponse = await fetch(this.#jsonUrl(this.dataset.cartUpdateUrl), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      await this.#checkWriteResponse(updateResponse);
    }
    const properties = {
      _b2b_product_id: String(this.config.productId),
      _b2b_moq: String(this.config.minimum),
      _b2b_tiers: JSON.stringify(this.tiers.map(({ quantity, discount }) => ({ quantity, discount }))),
      _b2b_pricing_group: String(this.config.pricingGroup),
      _b2b_pricing_label: String(this.config.pricingGroupLabel),
    };
    const additions = this.variants
      .filter((variant) => {
        if (!this.#canOrder(variant) || (this.quantities.get(String(variant.id)) || 0) <= 0) return false;
        const lines = existingByVariant.get(String(variant.id)) || [];
        return !lines.some((line) => String(line.properties?._b2b_pricing_group || '') === String(this.config.pricingGroup));
      })
      .map((variant) => ({ id: Number(variant.id), quantity: this.quantities.get(String(variant.id)), properties }));
    if (additions.length) {
      const addResponse = await fetch(this.#jsonUrl(this.dataset.cartAddUrl), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: additions }),
      });
      await this.#checkWriteResponse(addResponse);
    }
  }

  #jsonUrl(value) {
    const url = new URL(value, location.origin);
    if (!url.pathname.endsWith('.js')) url.pathname += '.js';
    return url.href;
  }

  async #checkWriteResponse(response) {
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload || payload.status >= 400) {
      throw new Error(payload?.description || payload?.message || this.config.labels.networkError);
    }
  }

  #showSuccess(totals, wasUpdate) {
    const success = this.querySelector('[data-success]');
    const copy = this.querySelector('[data-success-copy]');
    if (!success || !copy) return;
    copy.textContent = this.config.labels.savedShort.replace('{{ count }}', String(totals.quantity));
    success.hidden = false;
    this.successActive = true;
    this.successLabel = wasUpdate ? this.config.labels.updatedShort : this.config.labels.addedShort;
    this.querySelector('[data-submit]')?.setAttribute('data-state', 'success');
    clearTimeout(this.successTimer);
    this.successTimer = setTimeout(() => {
      this.successActive = false;
      this.querySelector('[data-submit]')?.removeAttribute('data-state');
      this.#updateSummary();
    }, 1800);
  }

  #clearSuccess() {
    clearTimeout(this.successTimer);
    this.successActive = false;
    this.querySelector('[data-submit]')?.removeAttribute('data-state');
    this.querySelector('[data-success]')?.setAttribute('hidden', '');
  }

  #showError(message) {
    this.#clearSuccess();
    const node = this.querySelector('[data-error]');
    if (!node) return;
    node.textContent = message;
    node.hidden = false;
  }

  #hideError() {
    this.#clearSuccess();
    const node = this.querySelector('[data-error]');
    if (node) node.hidden = true;
  }

  #escape(value) {
    const span = document.createElement('span');
    span.textContent = String(value ?? '');
    return span.innerHTML.replaceAll('"', '&quot;');
  }
}

if (!customElements.get('bulk-order-grid')) customElements.define('bulk-order-grid', BulkOrderGrid);
